import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CURRENCIES, currencyService } from '../services/currency';

const CurrencySelector: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentCurrency, setCurrentCurrency] = useState(currencyService.getCurrentCurrency());
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const unsubscribe = currencyService.subscribe((currency) => {
            setCurrentCurrency(currency);
        });

        return unsubscribe;
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const currentCurrencyInfo = currencyService.getCurrencyInfo(currentCurrency);

    const handleCurrencySelect = (code: string) => {
        currencyService.setCurrency(code);
        setIsOpen(false);
    };

    return (
        <div className="currency-selector" ref={dropdownRef}>
            <motion.button
                className="currency-button"
                onClick={() => setIsOpen(!isOpen)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
            >
                <span className="currency-flag">{currentCurrencyInfo.flag}</span>
                <span className="currency-code">{currentCurrencyInfo.code}</span>
                <motion.span
                    className="currency-arrow"
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                >
                    ▼
                </motion.span>
            </motion.button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="currency-dropdown"
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                    >
                        <div className="currency-list">
                            {CURRENCIES.map((currency) => (
                                <motion.button
                                    key={currency.code}
                                    className={`currency-item ${currency.code === currentCurrency ? 'active' : ''}`}
                                    onClick={() => handleCurrencySelect(currency.code)}
                                    whileHover={{ backgroundColor: 'var(--gray-50)' }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <span className="currency-flag">{currency.flag}</span>
                                    <div className="currency-info">
                                        <span className="currency-code">{currency.code}</span>
                                        <span className="currency-name">{currency.name}</span>
                                    </div>
                                    <span className="currency-symbol">{currency.symbol}</span>
                                </motion.button>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CurrencySelector;

